export interface DocumentType {
  key: string;
  label: string;
}

export interface LookupField {
  name: string;
  label: string;
  kind: 'text' | 'select';
  options?: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}

export type CarrierAction =
  | {
      type: 'policies';
      key: 'listPolicies';
      label: string;
      description: string;
      jobKey: string;
    }
  | {
      type: 'document';
      key: 'retrieveDocument' | 'retrieveDeclarationsPage';
      label: string;
      description: string;
      jobKey: string;
      documentTypes: DocumentType[];
    }
  | {
      // Find an insured's policies by last name, for when the policy number isn't known yet (carrier-browser tools).
      type: 'policySearch';
      key: 'searchPolicies';
      label: string;
      description: string;
      jobKey: string;
    }
  | {
      // A policy's dated document history — a listing only, nothing is downloaded (that's 'document').
      type: 'policyDocuments';
      key: 'listPolicyDocuments';
      label: string;
      description: string;
      jobKey: string;
    }
  | {
      type: 'quote';
      key: 'createQuote' | 'createHomeQuote';
      label: string;
      description: string;
      jobKey: string;
      disabled?: boolean;
      disabledReason?: string;
    }
  | {
      // A read-only lookup with a few input fields (selects or text) whose answer is a table of rows, a block of text per row,
      // or a file to download. The job's positional arguments are the field values in order (trailing blank optional fields are
      // left off). Used for the dashboards and reports the carrier-browser tools read.
      type: 'lookup';
      key: string; // unique per action: the page tracks which actions are switched on by key
      label: string;
      description: string;
      jobKey: string;
      fields: LookupField[];
      submitLabel?: string;
      /** Leading arguments sent before the field values (for jobs that take a mode word first). */
      fixedArgs?: string[];
    }
  | {
      // A carrier-side report/export that takes no input — just run it and
      // get a file back. Distinct from 'document', which always needs a
      // policy number + document type.
      type: 'report';
      key: 'runReport';
      label: string;
      description: string;
      jobKey: string;
    };

export type LineOfBusiness = 'Auto' | 'Home' | 'Commercial';

export const LINES_OF_BUSINESS: LineOfBusiness[] = ['Auto', 'Home', 'Commercial'];

/**
 * Whether the Create Quote action (and its "Leave browser open for me to
 * finish" switch) shows on the Carrier Access cards. Hidden for now; set to
 * true to bring it back. This only affects this page — the agent's
 * create_carrier_quote tool and the quote scripts are untouched.
 */
export const SHOW_QUOTE_ACTIONS = false;

export function visibleActions(carrier: CarrierDefinition): CarrierAction[] {
  return SHOW_QUOTE_ACTIONS ? carrier.actions : carrier.actions.filter((a) => a.type !== 'quote');
}

export interface CarrierDefinition {
  id: string;
  name: string;
  color: string;
  logoUrl?: string;
  /** Backing color behind logoUrl — defaults to white; override for logos drawn white-on-transparent. */
  logoBg?: string;
  lineOfBusiness: LineOfBusiness;
  /** Other lines the carrier's tools quote or service (shown on the tile with the main one; the line filter matches them too). */
  otherLines?: LineOfBusiness[];
  /**
   * Set for carriers whose new tools run in the hand-logged-in carrier browser (npm run carrier-browser) — shows
   * the "Logged-in carrier URL" box (CarrierSessionBar) on the carrier's panel.
   */
  browserSession?: { placeholder: string; startUrl: string; /** Only runs in the visible carrier browser: no "run hidden" switch. */ carrierBrowserOnly?: boolean };
  actions: CarrierAction[];
  /** Temporarily hidden from the Carrier Access grid without removing its catalog entry. */
  hidden?: boolean;
  /** Shown on the grid with a padlock and not clickable: its sign-in sends a one-time code to the agency (the server refuses agent runs too). */
  locked?: boolean;
  /** Portal can challenge logins with a 2FA/verification code (risk-based, so not guaranteed every run). */
  requiresVerificationCode?: boolean;
}

// documentTypes mirror each carrier's own DOCUMENTS map in
// automation/carriers/<carrier>/retrieveDocument.ts — kept in sync by hand
// since the frontend can't import server-side TS at build time.
const ALL_CARRIERS: CarrierDefinition[] = [
  {
    id: 'falcon',
    lineOfBusiness: 'Auto',
    name: 'Falcon',
    color: '#2A65F2',
    logoUrl: '/carrier-logos/falcon.png',
    // Optional: sign in once by hand in the carrier browser and its tools use that session (no password / 2FA step); without one they use the saved password.
    browserSession: { placeholder: 'https://agency.falconinsgroup.com/…', startUrl: 'https://agency.falconinsgroup.com/Home' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of active policies from your Falcon portal.',
        jobKey: 'falcon-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download any document from a policy’s Documents & Attachments tab.',
        jobKey: 'falcon-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declarations Page' },
          { key: 'owners-id-card', label: "Owner's ID Card" },
          { key: 'notice-of-installments', label: 'Notice of Installments' },
          { key: 'insured-mailing-page', label: 'Insured Mailing Page' },
          { key: 'renewal-notice-direct-bill', label: 'Renewal Notice (Direct Bill)' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new quote in Falcon.',
        jobKey: 'falcon-create-quote',
      },
    ],
  },
  {
    id: 'assuranceAmerica',
    lineOfBusiness: 'Auto',
    name: 'Assurance America',
    color: '#00966E',
    logoUrl: '/carrier-logos/assuranceAmerica.png',
    // Optional: sign in once by hand in the carrier browser and its tools use that session (no password / 2FA step); without one they use the saved password.
    browserSession: { placeholder: 'https://auto.assuranceamerica.com/…', startUrl: 'https://auto.assuranceamerica.com/login' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of active policies from your Assurance America portal.',
        jobKey: 'assurance-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download any document from a policy’s Documents tab.',
        jobKey: 'assurance-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declarations Page' },
          { key: 'id-card', label: 'ID Card' },
          { key: 'auto-pay-authorization', label: 'Authorization for Automatic Pay Plan' },
          { key: 'named-driver-exclusion', label: 'Named Driver Exclusion' },
          { key: 'payment-schedule', label: 'Payment Schedule' },
          { key: 'auto-inspection', label: 'Auto Inspection' },
        ],
      },
      {
        type: 'lookup',
        key: 'assuranceReports',
        label: 'Reports',
        description: 'Payments received, EFT payments, commission statement, policy activity, producer production, policy status and claims detail for a date range (last 31 days if blank).',
        jobKey: 'assurance-report',
        submitLabel: 'Run',
        fields: [
          {
            name: 'report',
            label: 'Report',
            kind: 'select',
            options: [
              { value: 'policy-activity', label: 'Policy activity (cancellations, renewals, new business)' },
              { value: 'policy-status', label: 'Policy status counts' },
              { value: 'producer-production', label: 'Producer production' },
              { value: 'payments-received', label: 'Payments received' },
              { value: 'commission-statement', label: 'Commission statement' },
              { value: 'agent-eft-payments', label: 'Agent EFT payments' },
              { value: 'claims-detail', label: 'Claims detail' },
            ],
          },
          { name: 'startDate', label: 'Start date', kind: 'text', placeholder: 'Start MM/DD/YYYY (optional)' },
          { name: 'endDate', label: 'End date', kind: 'text', placeholder: 'End MM/DD/YYYY (optional)' },
        ],
      },
      {
        type: 'lookup',
        key: 'assuranceFindPolicy',
        label: 'Find a Policy',
        description: 'Search policies and quotes by insured name, policy number or quote number.',
        jobKey: 'assurance-policy',
        submitLabel: 'Search',
        fixedArgs: ['find'],
        fields: [{ name: 'query', label: 'Name, policy # or quote #', kind: 'text', placeholder: 'Name, policy # or quote #', required: true }],
      },
      {
        type: 'lookup',
        key: 'assurancePolicyDetail',
        label: 'Policy Detail',
        description: 'A bound policy’s summary, billing, drivers, coverages, activity and notes (no dates of birth or license numbers).',
        jobKey: 'assurance-policy',
        submitLabel: 'Look up',
        fixedArgs: ['detail'],
        fields: [{ name: 'policyNumber', label: 'Policy number', kind: 'text', placeholder: 'e.g. POK2641825-1', required: true }],
      },
      {
        type: 'lookup',
        key: 'assuranceForms',
        label: 'Forms & Manuals',
        description: 'Download a form or agency manual from the Assurance America library.',
        jobKey: 'assurance-forms',
        submitLabel: 'Download',
        fixedArgs: ['download'],
        fields: [
          {
            name: 'name',
            label: 'Form',
            kind: 'select',
            options: [
              { value: 'Oklahoma Agency Manual', label: 'Oklahoma Agency Manual' },
              { value: 'Statement of No Accidents', label: 'Statement of No Accidents' },
              { value: 'Policy Contract', label: 'Policy Contract' },
              { value: 'Agency Download Signup Request Form', label: 'Agency Download Signup Request Form' },
              { value: 'Agent EFT Change Form', label: 'Agent EFT Change Form' },
              { value: 'InsureMax Oklahoma Agency Manual: Renters Protect Program', label: 'InsureMax Renters Protect Program manual' },
            ],
          },
          {
            name: 'company',
            label: 'Company',
            kind: 'select',
            options: [
              { value: 'AssuranceAmerica', label: 'AssuranceAmerica' },
              { value: 'InsureMax', label: 'InsureMax' },
            ],
          },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new quote in Assurance America.',
        jobKey: 'assurance-create-quote',
      },
    ],
  },
  {
    id: 'libertyMutual',
    requiresVerificationCode: true,
    lineOfBusiness: 'Auto',
    otherLines: ['Home'],
    name: 'Liberty Mutual',
    color: '#FFD200',
    logoUrl: '/carrier-logos/libertyMutual.png',
    // Liberty's new search / document-history / declarations tools need the carrier browser (heavy 2FA — a person
    // logs in by hand). The stored-login actions below are unchanged.
    browserSession: {
      placeholder: 'https://personal-agent.libertymutual.com/…',
      startUrl: 'https://personal-agent.libertymutual.com/start?loginmode=agent',
    },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull recently active policies from your Liberty Mutual agency dashboard.',
        jobKey: 'liberty-mutual-policies',
      },
      {
        type: 'policySearch',
        key: 'searchPolicies',
        label: 'Find a Policy by Last Name (carrier browser)',
        description: 'Look up a customer\'s Liberty Mutual policies when you don\'t have the policy number. Needs the carrier browser.',
        jobKey: 'liberty-search-policies',
      },
      {
        type: 'policyDocuments',
        key: 'listPolicyDocuments',
        label: 'View Policy Document History (carrier browser)',
        description: 'List the renewals, endorsements, letters and billing documents Liberty Mutual has on file for a policy. Needs the carrier browser.',
        jobKey: 'liberty-policy-documents',
      },
      {
        // Placeholder definition of "declarations page" for this carrier — see retrieveDeclarationsPage in
        // automation/lib/libertyMutualPortal.ts.
        type: 'document',
        key: 'retrieveDeclarationsPage',
        label: 'Retrieve Declarations Page (carrier browser)',
        description: 'Download the latest renewal packet (declarations included) for a policy. Needs the carrier browser.',
        jobKey: 'liberty-declarations-page',
        documentTypes: [{ key: 'declarations-page', label: 'Declarations Page (latest renewal packet)' }],
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Search and download a document for a policy from DocZilla.',
        jobKey: 'liberty-mutual-document',
        documentTypes: [
          { key: 'RENEWAL', label: 'Renewal' },
          { key: 'REINSTATEMENT', label: 'Reinstatement w/ Policy' },
          { key: 'Letter', label: 'Letter (Underwriting Info)' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Personal Auto quote in Liberty Mutual.',
        jobKey: 'liberty-mutual-create-quote',
      },
    ],
  },
  {
    id: 'nationalGeneral',
    requiresVerificationCode: true,
    lineOfBusiness: 'Auto',
    name: 'National General',
    color: '#00966E',
    logoUrl: '/carrier-logos/nationalGeneral.png',
    // Log in once in the carrier browser; the session is saved into this carrier's own saved-login file and its runs stay hidden.
    browserSession: { placeholder: 'https://www.natgenagency.com/…', startUrl: 'https://www.natgenagency.com/' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull recently bound policies (last 30 days) from your National General agency dashboard.',
        jobKey: 'national-general-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download a document for a policy from National General.',
        jobKey: 'national-general-document',
        documentTypes: [
          { key: 'initial-policy-documents', label: 'Initial Policy Documents' },
          { key: 'new-business-packet', label: 'New Business Packet' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Personal Auto quote in National General.',
        jobKey: 'national-general-create-quote',
      },
    ],
  },
  {
    id: 'harbor',
    lineOfBusiness: 'Auto',
    name: 'Harbor',
    color: '#8854BC',
    logoUrl: '/carrier-logos/harbor.png',
    // Optional: sign in once by hand in the carrier browser and its tools use that session (no password / 2FA step); without one they use the saved password.
    browserSession: { placeholder: 'https://agentweb.swgen.com/…', startUrl: 'https://agentweb.swgen.com' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of active policies from your Harbor portal.',
        jobKey: 'harbor-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download any of Harbor’s confirmed-working document types.',
        jobKey: 'harbor-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declarations Page' },
          { key: 'id-card', label: 'ID Card' },
          { key: 'verification-letter', label: 'Insurance Verification Letter' },
          { key: 'roadside-form', label: 'Roadside Assistance Form' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new quote in Harbor.',
        jobKey: 'harbor-create-quote',
      },
    ],
  },
  {
    id: 'geico',
    lineOfBusiness: 'Auto',
    name: 'GEICO',
    color: '#0057B8',
    logoUrl: '/carrier-logos/geico.png',
    // GEICO's sign-in is its own identity service (a code may be asked): log in once by hand in the carrier browser; there is no stored password.
    browserSession: { placeholder: 'https://gateway2.geico.com/…', startUrl: 'https://gateway2.geico.com/', carrierBrowserOnly: true },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the newest policies from your GEICO Gateway.',
        jobKey: 'geico-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download a policy’s Declaration Page, ID card or Policy Contract from GEICO.',
        jobKey: 'geico-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declaration Page' },
          { key: 'id-card', label: 'Insurance ID Card' },
          { key: 'policy-contract', label: 'Policy Contract' },
        ],
      },
    ],
  },
  {
    id: 'openly',
    requiresVerificationCode: true,
    lineOfBusiness: 'Home',
    name: 'Openly',
    color: '#FF6820',
    logoUrl: '/carrier-logos/openly.png',
    // Log in once in the carrier browser; the session is saved into this carrier's own saved-login file and its runs stay hidden.
    browserSession: { placeholder: 'https://app.openly.com/…', startUrl: 'https://app.openly.com' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of active policies from your Openly portal.',
        jobKey: 'openly-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download any document from a bound policy’s Documents tab.',
        jobKey: 'openly-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declarations Page' },
          { key: 'policy', label: 'Policy' },
          { key: 'mortgage-bill', label: 'Mortgage Bill' },
          { key: 'transition-reminder', label: 'Transition Reminder' },
        ],
      },
      {
        type: 'lookup',
        key: 'openlyDashboards',
        label: 'Dashboards',
        description: 'Upcoming renewals (with the premium change), eligible quotes that were never bound, non-renewals, or a search of every policy and quote.',
        jobKey: 'openly-dashboard',
        submitLabel: 'Show',
        fields: [
          {
            name: 'dashboard',
            label: 'Dashboard',
            kind: 'select',
            options: [
              { value: 'upcoming-renewals', label: 'Upcoming renewals' },
              { value: 'eligible-quotes', label: 'Eligible quotes (not bound)' },
              { value: 'non-renewals', label: 'Non-renewals' },
              { value: 'search', label: 'Search policies and quotes' },
            ],
          },
          { name: 'query', label: 'Search for', kind: 'text', placeholder: 'Search only: policy number, name or address' },
        ],
      },
      {
        type: 'lookup',
        key: 'openlyPolicyDetail',
        label: 'Policy Detail',
        description: 'An issued policy’s summary (renewal premium change, deductibles, replacement cost), billing ledger, transaction history and claims.',
        jobKey: 'openly-policy',
        submitLabel: 'Look up',
        fields: [{ name: 'policyNumber', label: 'Policy number', kind: 'text', placeholder: 'e.g. BQ01-NAYVQRK', required: true }],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Homeowners quote in Openly (stops before Bind). Openly runs a real insurance-score check on the applicant, so the state disclosure must be read to them first.',
        jobKey: 'openly-create-quote',
      },
    ],
  },
  {
    id: 'farmersMutual',
    name: 'Farmers Mutual',
    color: '#00966E',
    logoUrl: '/carrier-logos/farmersMutual.jpg',
    lineOfBusiness: 'Home',
    hidden: true,
    actions: [
      {
        type: 'report',
        key: 'runReport',
        label: 'X-Date Report',
        description: 'Export the X-Date Quote Manager report — expiring quote opportunities for the coming month.',
        jobKey: 'farmers-mutual-xdate-report',
      },
    ],
  },
  {
    id: 'mercury',
    name: 'Mercury Insurance',
    color: '#2A65F2',
    logoUrl: '/carrier-logos/mercury.png',
    // Optional: sign in once by hand in the carrier browser and its tools use that session (no password / 2FA step); without one they use the saved password.
    browserSession: { placeholder: 'https://transcendac.mercuryinsurance.com/…', startUrl: 'https://transcendac.mercuryinsurance.com/pc/PolicyCenter.do' },
    lineOfBusiness: 'Auto',
    otherLines: ['Home'],
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of in-force policies from your Mercury AgentCenter portal.',
        jobKey: 'mercury-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download the most recent Declarations Page or ID Card for a policy.',
        jobKey: 'mercury-document',
        documentTypes: [
          { key: 'declarations-page', label: 'Declarations Page' },
          { key: 'id-card', label: 'ID Card' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Personal Auto quote in Mercury AgentCenter. Credit, e-sign, and paperless enrollment are always declined.',
        jobKey: 'mercury-create-quote',
      },
    ],
  },
  {
    // ForAgentsOnly.com — Progressive's agent portal, distinct from
    // progressive.com (the customer site). Create Quote is live-tested
    // end to end through Point of Sale (real premium confirmed). View
    // Policy List is live-tested against this agency's real book (332
    // policies, exact match against the portal's own count). Document
    // retrieval (ID card, dec pages, VOI, renewal dec page) is built in
    // automation/lib/progressiveDocuments.ts but not yet live-verified
    // against a real policy, so it isn't wired in here yet.
    id: 'progressive',
    requiresVerificationCode: true,
    name: 'Progressive',
    color: '#0A5EB0',
    logoUrl: '/carrier-logos/progressive.png',
    lineOfBusiness: 'Auto',
    otherLines: ['Home', 'Commercial'],
    browserSession: { placeholder: 'https://www.foragentsonly.com/home/…', startUrl: 'https://www.foragentsonly.com' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the list of active policies from your Progressive (ForAgentsOnly) portal.',
        jobKey: 'progressive-policies',
      },
      {
        type: 'policySearch',
        key: 'searchPolicies',
        label: 'Find a Policy by Last Name',
        description: 'Look up an insured\'s Progressive policies when you don\'t have the policy number.',
        jobKey: 'progressive-search-policies',
      },
      {
        type: 'policyDocuments',
        key: 'listPolicyDocuments',
        label: 'View Policy Document History',
        description: 'List the statements, notices and declarations pages Progressive has on file for a policy.',
        jobKey: 'progressive-policy-documents',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download the Declarations Page for a policy.',
        jobKey: 'progressive-document',
        documentTypes: [{ key: 'declarations-page', label: 'Declarations Page' }],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Personal Auto quote in Progressive (ForAgentsOnly). Builds through Point of Sale with a full premium — never advances to Final Sale, and CLUE/MVR reports are not ordered.',
        jobKey: 'progressive-create-quote',
      },
      {
        // Mapped live 2026-10-12 — see automation/carriers/progressive/HOME_QUOTE_MAPPING.md.
        type: 'quote',
        key: 'createHomeQuote',
        label: 'Create Home Quote',
        description: 'Start a new Homeowners (HO3) quote in Progressive (ForAgentsOnly). Builds through Portfolio with a preliminary annual premium — no claims or credit reports are ordered and nothing is bound.',
        jobKey: 'progressive-create-home-quote',
      },
    ],
  },
  {
    // Travelers' For Agents portal (foragents.travelers.com). Its Okta sign-in
    // refuses scripted submissions, so a person signs in once in a visible
    // window (session-tools/travelersBootstrap.ts) and the automation reuses that
    // trusted session for about 30 days — see lib/travelersPortal.ts. Only
    // View Policy List and Retrieve a Document are built; both work off the
    // "Recently Issued Policies" list on the portal home.
    id: 'travelers',
    requiresVerificationCode: true,
    name: 'Travelers',
    color: '#E31837',
    lineOfBusiness: 'Auto',
    logoUrl: '/carrier-logos/travelers.png',
    // Log in once in the carrier browser; the session is saved into this carrier's own saved-login file and its runs stay hidden.
    browserSession: { placeholder: 'https://foragents.travelers.com/…', startUrl: 'https://foragents.travelers.com/' },
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Pull the recently issued policies from your Travelers For Agents portal.',
        jobKey: 'travelers-policies',
      },
      {
        type: 'document',
        key: 'retrieveDocument',
        label: 'Retrieve a Document',
        description: 'Download a document for a recently issued policy from Travelers.',
        jobKey: 'travelers-document',
        documentTypes: [
          { key: 'policy', label: 'Policy (full packet)' },
          { key: 'agent-proposal', label: 'Agent Proposal' },
        ],
      },
      {
        type: 'quote',
        key: 'createQuote',
        label: 'Create Quote',
        description: 'Start a new Personal Auto quote in Travelers (new customer; rated on an assumed score, never bound).',
        jobKey: 'travelers-create-quote',
      },
    ],
  },
  {
    // Demo carrier, not a real portal — intentionally has no .env.local
    // entry (see automation/mcp-server/server.mjs CREDENTIAL_ENV_VARS), so
    // it always shows "Not connected" with a live Connect button, no matter
    // which agency is signed in. Lets anyone see the per-agency login flow
    // (CarrierCredentialModal) without touching a real, working carrier.
    id: 'mockingbird',
    hidden: true,
    name: 'Mockingbird Insurance',
    color: '#8B5CF6',
    lineOfBusiness: 'Auto',
    actions: [
      {
        type: 'policies',
        key: 'listPolicies',
        label: 'View Policy List',
        description: 'Demo carrier — shows what the "Connect" login looks like for an agency that hasn’t linked this carrier yet.',
        jobKey: 'mockingbird-policies',
      },
    ],
  },
];

/**
 * Carriers kept out of the Carrier Access screens for now (their tile, "Log in to all carriers", the quote forms). Travelers: its customer
 * search has been down, so quoting there cannot be finished. Remove the id here to bring it back; its automation is untouched.
 */
const HIDDEN_CARRIER_IDS = new Set<string>(); // 2026-10-28: Travelers and National General are shown again but locked for agents on the server (AGENT_LOCKED_CARRIERS in server.mjs)

const LOCKED_CARRIER_IDS = new Set<string>(['travelers', 'nationalGeneral']); // same two as AGENT_LOCKED_CARRIERS in automation/mcp-server/server.mjs

export const CARRIER_CATALOG: CarrierDefinition[] = ALL_CARRIERS.filter((c) => !HIDDEN_CARRIER_IDS.has(c.id)).map((c) => (LOCKED_CARRIER_IDS.has(c.id) ? { ...c, locked: true } : c));

/**
 * A few short, plain-English lines describing what a carrier's tile can do
 * — shown right on the tile (see CarrierAccessPage) so an agency can see
 * "pulls Dec Pages, ID Cards" etc. at a glance instead of having to expand
 * the tile and open the Automations dropdown first.
 */
export function carrierCapabilitySummary(carrier: CarrierDefinition): string[] {
  const lines: string[] = [];
  for (const action of visibleActions(carrier)) {
    if (action.type === 'policies') {
      lines.push('View policy list');
    } else if (action.type === 'document') {
      const labels = action.documentTypes.slice(0, 3).map((d) => d.label);
      const extra = action.documentTypes.length > 3 ? ` +${action.documentTypes.length - 3} more` : '';
      lines.push(`Pull ${labels.join(', ')}${extra}`);
    } else if (action.type === 'quote') {
      lines.push('Create quotes');
    } else if (action.type === 'report') {
      lines.push(action.label);
    }
  }
  return lines;
}


/** Every line a carrier covers: its main line first, then the others. */
export const carrierLines = (c: Pick<CarrierDefinition, 'lineOfBusiness' | 'otherLines'>): LineOfBusiness[] => [c.lineOfBusiness, ...(c.otherLines ?? []).filter((l) => l !== c.lineOfBusiness)];
